import { onlyDigits, validCep8, slugify } from "./utils";
import { findActiveOverlaps } from "./cepRangeAudit";

export function detectDelimiter(text) {
  const lines = String(text || "").split(/\r?\n/).slice(0, 5);
  let commas = 0;
  let semicolons = 0;
  for (const l of lines) {
    commas += (l.match(/,/g) || []).length;
    semicolons += (l.match(/;/g) || []).length;
  }
  return semicolons > commas ? ";" : ",";
}

export function parseCSV(text, delimiter) {
  const rows = [];
  let i = 0;
  let cur = [];
  let field = "";
  let inQuotes = false;

  const pushField = () => {
    cur.push(field);
    field = "";
  };
  const pushRow = () => {
    rows.push(cur);
    cur = [];
  };

  const src = String(text || "");
  while (i < src.length) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += ch;
      i++;
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (ch === delimiter) {
      pushField();
      i++;
      continue;
    }
    if (ch === "\n") {
      pushField();
      pushRow();
      i++;
      continue;
    }
    if (ch === "\r") {
      i++;
      continue;
    }
    field += ch;
    i++;
  }
  pushField();
  if (cur.length > 1 || (cur.length === 1 && cur[0] !== "")) pushRow();
  return rows;
}

function normHeader(s) {
  return String(s ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_");
}

function pickIndex(headers, preferred, weak) {
  const H = headers.map(normHeader);
  for (const k of preferred) {
    const idx = H.indexOf(k);
    if (idx >= 0) return idx;
  }
  for (const k of weak) {
    const idx = H.indexOf(k);
    if (idx >= 0) return idx;
  }
  return -1;
}

/** Prefer specific CEP headers; short aliases only as fallback. */
export function buildHeaderMap(headers) {
  const start_cep = pickIndex(
    headers,
    ["cep_inicial", "inicio_cep", "cep_de", "cep_inicio", "start_cep", "cep_inicio_de"],
    ["inicio", "start", "inicial"]
  );
  const end_cep = pickIndex(
    headers,
    ["cep_final", "final_cep", "cep_ate", "cep_fim", "end_cep", "cep_fim_ate"],
    ["fim", "ate", "end", "final"]
  );
  const region_key = pickIndex(
    headers,
    ["region_key", "marca", "region"],
    []
  );
  const target = pickIndex(
    headers,
    ["destino", "target_cod_interno", "target_codigo_interno", "slug", "destino_slug", "target"],
    []
  );
  return { start_cep, end_cep, region_key, target };
}

export function dupeKey(region_key, start, end, target) {
  return [
    String(region_key || "").trim(),
    onlyDigits(start),
    onlyDigits(end),
    String(target || "").trim(),
  ].join("|");
}

/**
 * Parse CSV into a preview: toAdd / errors / duplicates / overlapBlocked.
 * Does not mutate existing ranges.
 */
export function previewCepImport({
  text,
  defaultRegion = "",
  defaultTarget = "",
  existingRanges = [],
}) {
  const regionDefault = String(defaultRegion || "").trim();
  const targetDefault = slugify(defaultTarget || "");

  if (!String(text || "").trim()) {
    return { headerError: "Arquivo vazio.", toAdd: [], errors: [], duplicatesSkipped: 0, overlapBlocked: [] };
  }

  const delimiter = detectDelimiter(text);
  const rows = parseCSV(text, delimiter);
  if (!rows.length) {
    return { headerError: "Arquivo vazio.", toAdd: [], errors: [], duplicatesSkipped: 0, overlapBlocked: [] };
  }

  const headers = rows[0];
  const dataRows = rows.slice(1).filter((row) => row.some((c) => String(c || "").trim() !== ""));
  if (!dataRows.length) {
    return {
      headerError: "Nenhum dado encontrado após o cabeçalho.",
      toAdd: [],
      errors: [],
      duplicatesSkipped: 0,
      overlapBlocked: [],
    };
  }

  const hmap = buildHeaderMap(headers);
  if (hmap.start_cep < 0 || hmap.end_cep < 0) {
    return {
      headerError:
        "Não encontrei colunas de CEP (inicial/final). Use cabeçalhos como 'CEP Inicial' e 'CEP Final'.",
      toAdd: [],
      errors: [],
      duplicatesSkipped: 0,
      overlapBlocked: [],
    };
  }

  const existing = new Set(
    (existingRanges || []).map((r) =>
      dupeKey(r.region_key, r.start_cep, r.end_cep, r.target_cod_interno)
    )
  );
  const seen = new Set();
  const toAdd = [];
  const errors = [];
  const overlapBlocked = [];
  let duplicatesSkipped = 0;
  const acceptedForOverlap = [...(existingRanges || [])];

  for (let i = 0; i < dataRows.length; i++) {
    const row = dataRows[i];
    const rowNum = i + 2;
    const start_cep = onlyDigits(row[hmap.start_cep] || "");
    const end_cep = onlyDigits(row[hmap.end_cep] || "");
    const region_key =
      hmap.region_key >= 0
        ? slugify(row[hmap.region_key] || "") || regionDefault
        : regionDefault;
    const target =
      hmap.target >= 0
        ? slugify(row[hmap.target] || "") || targetDefault
        : targetDefault;

    if (!region_key) {
      errors.push(`L${rowNum}: marca vazia.`);
      continue;
    }
    if (!target) {
      errors.push(`L${rowNum}: destino vazio.`);
      continue;
    }
    if (!validCep8(start_cep)) {
      errors.push(`L${rowNum}: CEP inicial inválido (${row[hmap.start_cep] ?? ""}).`);
      continue;
    }
    if (!validCep8(end_cep)) {
      errors.push(`L${rowNum}: CEP final inválido (${row[hmap.end_cep] ?? ""}).`);
      continue;
    }
    if (Number(start_cep) > Number(end_cep)) {
      errors.push(`L${rowNum}: CEP inicial maior que o final (${start_cep} > ${end_cep}).`);
      continue;
    }

    const key = dupeKey(region_key, start_cep, end_cep, target);
    if (seen.has(key) || existing.has(key)) {
      duplicatesSkipped += 1;
      continue;
    }

    const candidate = {
      region_key,
      start_cep,
      end_cep,
      target_cod_interno: target,
      status: 1,
    };
    const overlaps = findActiveOverlaps(candidate, acceptedForOverlap);
    if (overlaps.length) {
      overlapBlocked.push({
        rowNum,
        ...candidate,
        overlaps,
        message: `L${rowNum}: sobrepõe faixa ativa ${overlaps[0].region_key} ${overlaps[0].start_cep}–${overlaps[0].end_cep} → ${overlaps[0].target_cod_interno}.`,
      });
      continue;
    }

    seen.add(key);
    toAdd.push(candidate);
    acceptedForOverlap.push(candidate);
  }

  return {
    headerError: null,
    delimiter,
    toAdd,
    errors,
    duplicatesSkipped,
    overlapBlocked,
  };
}
